import React from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import { useAuth } from "../context/AuthContext";
import { Upload, PenLine, BadgeCheck, Send, ShieldCheck, DollarSign } from "lucide-react";

const STEPS = [
    { icon: Upload, title: "1. Submit your film", copy: "Add title, metadata, cast, synopsis, poster, trailer link, and chain-of-title documents in the Upload Studio." },
    { icon: PenLine, title: "2. Sign the assignment deed", copy: "You assign copyright to Silver Screen Xchange under Section 18 of the Indian Copyright Act, 1957 — for onward exclusive transfer." },
    { icon: BadgeCheck, title: "3. Legal review", copy: "Our team verifies chain-of-title, warranties and underlying clearances before your film is listed on the marketplace." },
    { icon: Send, title: "4. Buyers open deal rooms", copy: "Qualified, KYC'd buyers request a deal room, negotiate and sign the exclusive Purchase Agreement." },
    { icon: DollarSign, title: "5. Paid to escrow", copy: "Stripe payments flow into escrow. Once cleared, funds release to you — minus our transparent marketplace fee." },
    { icon: ShieldCheck, title: "6. Transfer recorded", copy: "A signed Transfer Record is issued to the buyer, and your film's ownership is officially moved. Vault hand-off is automated." },
];

export default function SellYourRights() {
    const { user } = useAuth();
    const studioHref = user?.role === "creator" ? "/creator" : "/register?role=creator";

    return (
        <div className="min-h-screen bg-ink-300 text-silver-100">
            <Navbar />
            <div className="max-w-[1200px] mx-auto px-4 md:px-10 pt-14 md:pt-20 pb-20">
                <div className="max-w-3xl mb-10 md:mb-14">
                    <div className="text-[10px] md:text-[11px] tracking-[0.3em] uppercase text-platinum mb-3">
                        Sell your rights
                    </div>
                    <h1 className="font-display text-4xl sm:text-5xl md:text-6xl leading-[1.05]">
                        Get paid once, <span className="silver-text italic">worldwide.</span>
                    </h1>
                    <p className="mt-5 text-silver-300 text-sm md:text-base leading-relaxed max-w-2xl">
                        List your feature, short, series or documentary on Silver Screen Xchange. We handle
                        legal paperwork, chain-of-title verification, e-sign, escrow and global
                        buyer verification — so you can focus on making films.
                    </p>
                    <div className="mt-8 flex flex-wrap gap-3">
                        <Link
                            to={studioHref}
                            className="inline-flex items-center gap-2 bg-platinum hover:bg-platinum-200 text-ink-300 font-medium px-6 py-3 rounded-sm uppercase tracking-widest text-xs"
                            data-testid="sell-cta"
                        >
                            {user?.role === "creator" ? "Open Upload Studio" : "Create creator account"}
                        </Link>
                        <Link
                            to="/contact"
                            className="inline-flex items-center gap-2 border border-white/15 hover:border-platinum text-silver-200 hover:text-white px-6 py-3 rounded-sm uppercase tracking-widest text-xs"
                        >
                            Talk to onboarding
                        </Link>
                    </div>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {STEPS.map((s, i) => (
                        <div key={i} className="glass p-6 rounded-sm" data-testid={`sell-step-${i}`}>
                            <s.icon className="w-6 h-6 text-platinum mb-4" />
                            <h3 className="font-display text-xl mb-2">{s.title}</h3>
                            <p className="text-silver-400 text-sm leading-relaxed">{s.copy}</p>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
